/**
 * Prompt Service
 * Handles fetching and managing addon prompts from the backend
 */

import { APIEndpoints } from '../config/APIEndpoints';

export interface AddonPrompt {
  title: string;
  /** Display title for onboarding (personalized assistant). Optional in DB. */
  slug?: string | null;
  content: string;
  createdAt: string;
  updatedAt: string;
  userId?: string | null;
  model?: string | null;
  provider?: string | null;
  temperature?: number | null;
}

export interface AddonPromptsResponse {
  success: boolean;
  count: number;
  prompts: AddonPrompt[];
}

export interface SingleAddonPromptResponse {
  success: boolean;
  prompt?: AddonPrompt;
  message?: string;
}

export interface CacheStatusResponse {
  success: boolean;
  is_connected: boolean;
  cached_prompts: number;
  cached_keys: string[];
  cached_addon_prompts: number;
  addon_prompt_titles: string[];
  prompt_versions: Record<string, string>;
  addon_versions: Record<string, string>;
  version_checker_running: boolean;
  check_interval: number;
  source?: string;
  table?: string;
}

class PromptService {
  /**
   * Fetch all addon prompts
   * @param forceRefresh - If true, bypass cache and fetch fresh from DB
   */
  async getAddonPrompts(forceRefresh: boolean = false): Promise<AddonPromptsResponse> {
    try {
      const response = await fetch(APIEndpoints.PROMPTS.ADDONS.LIST(forceRefresh));
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching addon prompts:', error);
      return {
        success: false,
        count: 0,
        prompts: []
      };
    }
  }

  /**
   * Fetch a specific addon prompt by title
   * @param title - Title of the addon prompt
   */
  async getAddonPromptByTitle(title: string): Promise<SingleAddonPromptResponse> {
    try {
      const response = await fetch(APIEndpoints.PROMPTS.ADDONS.GET_BY_TITLE(title));
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error fetching addon prompt '${title}':`, error);
      return {
        success: false,
        message: `Failed to fetch addon prompt: ${error}`
      };
    }
  }

  /**
   * Add or update an addon prompt (Admin only)
   * @param title - Title of the addon prompt
   * @param content - Content of the prompt
   * @param userId - Optional user ID
   */
  async addAddonPrompt(
    title: string,
    content: string,
    userId?: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const params = new URLSearchParams({
        title,
        content,
        ...(userId && { user_id: userId })
      });

      const response = await fetch(
        `${APIEndpoints.PROMPTS.ADMIN.ADD_ADDON}?${params.toString()}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error adding addon prompt:', error);
      return {
        success: false,
        message: `Failed to add addon prompt: ${error}`
      };
    }
  }

  /**
   * Get cache status (Admin only)
   */
  async getCacheStatus(): Promise<CacheStatusResponse | null> {
    try {
      const response = await fetch(APIEndpoints.PROMPTS.ADMIN.CACHE_STATUS);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching cache status:', error);
      return null;
    }
  }

  /**
   * Manually refresh cache (Admin only)
   */
  async refreshCache(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(APIEndpoints.PROMPTS.ADMIN.CACHE_REFRESH, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error refreshing cache:', error);
      return {
        success: false,
        message: `Failed to refresh cache: ${error}`
      };
    }
  }

  /**
   * Clear cache (Admin only)
   */
  async clearCache(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(APIEndpoints.PROMPTS.ADMIN.CACHE_CLEAR, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error clearing cache:', error);
      return {
        success: false,
        message: `Failed to clear cache: ${error}`
      };
    }
  }

  /**
   * Update version checker interval (Admin only)
   * @param interval - New interval in seconds (minimum 5)
   */
  async setVersionCheckerInterval(interval: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(
        `${APIEndpoints.PROMPTS.ADMIN.VERSION_CHECKER_INTERVAL}?interval=${interval}`,
        {
          method: 'POST'
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error setting version checker interval:', error);
      return {
        success: false,
        message: `Failed to set interval: ${error}`
      };
    }
  }
}

// Export singleton instance
export const promptService = new PromptService();
export default promptService;

